import React from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import classNames from "classnames";
import i18n from "i18next";
import { LuEye, LuEyeOff } from "react-icons/lu";

import style from "./_itemRenderer.scss";
import EditorIndexTitle from "../editor_index_title/EditorIndexTitle";
import { isSlide, isPage } from "../../../../common/utils";
import iconPDF from "../../../../dist/images/file-pdf.svg";
import CarouselList from "../carousel_list/CarouselList";

const getHierarchicalIndex = (id, path, navItems) => {
  const indexParts = [];
  const currentItem = navItems[id];

  if (!currentItem) {
    return "";
  }

  for (let i = 0; i < path.length; i++) {
    const parentId = path[i];

    if (parentId === 0) {
      continue;
    }

    const parent = navItems[parentId];
    if (parent && parent.parent !== undefined) {
      const grandParent = navItems[parent.parent];
      if (grandParent && grandParent.children) {
        const parentIndex = grandParent.children.indexOf(parentId) + 1;
        indexParts.push(parentIndex);
      }
    }
  }

  if (currentItem.parent !== undefined) {
    const parent = navItems[currentItem.parent];
    if (parent && parent.children) {
      const currentIndex = parent.children.indexOf(id) + 1;
      indexParts.push(currentIndex);
    }
  }

  return indexParts.length > 0 ? indexParts.join(".") : "";
};

const Folder = ({
  name,
  collapsed,
  index,
  path,
  onToggleCollapse,
  id,
  navItems,
  onNavItemNameChanged,
  viewToolbars,
  indexSelected,
  containedViewSelected,
}) => {
  const handleClick = () => {
    onToggleCollapse(index);
  };
  const classCollapsed = collapsed ? "collapsed" : "";
  const classIndexSelected = id === indexSelected ? " classIndexSelected " : " ";
  const itemNumber = getHierarchicalIndex(id, path, navItems);

  const indentedClass = path.length > 0 ? "indented" : "";
  const isSelected = id === indexSelected;

  return (
    <div
      className={`folder navItemBlock ${classCollapsed} ${classIndexSelected} ${indentedClass}`}
      style={path.length > 0 ? { paddingLeft: path.length * 20 } : {}}
    >
      <div className={`item-number ${isSelected ? "selected" : ""}`}>{itemNumber}</div>
      <div className="item-divider" />
      <EditorIndexTitle
        id={id}
        index=""
        title={viewToolbars?.[id]?.viewName || ""}
        hidden={navItems[id].hidden}
        selected={indexSelected}
        onNameChanged={onNavItemNameChanged}
      />
      <button className="toggleCollapseHandle" onClick={handleClick}>
        <i className={collapsed ? "material-icons collapsed" : "material-icons "}>
          keyboard_arrow_down
        </i>
      </button>
    </div>
  );
};

const File = ({
  name,
  collapsed,
  id,
  path,
  navItems,
  onNavItemNameChanged,
  onNavItemToggled,
  viewToolbars,
  indexSelected,
  containedViewSelected,
}) => {
  const classIndexSelected = id === indexSelected ? " classIndexSelected " : " ";
  const classContainedViewSelected = id === containedViewSelected ? " selected " : " notSelected ";
  const itemNumber = getHierarchicalIndex(id, path, navItems);

  const indentedClass = path.length > 0 ? "indented" : "";
  const isSelected = id === indexSelected;
  const isHidden = navItems[id]?.hidden;

  // At least one scene must stay visible: block hiding the last visible page.
  const visiblePageCount = React.useMemo(
    () => Object.keys(navItems).filter((nid) => isPage(nid) && !navItems[nid].hidden).length,
    [navItems]
  );
  const isLastVisiblePage = !isHidden && visiblePageCount <= 1;

  const [showLastSceneWarning, setShowLastSceneWarning] = React.useState(false);
  React.useEffect(() => {
    if (!showLastSceneWarning) return undefined;
    const t = setTimeout(() => setShowLastSceneWarning(false), 3000);
    return () => clearTimeout(t);
  }, [showLastSceneWarning]);

  return (
    <div
      className={`file navItemBlock ${classIndexSelected} ${classContainedViewSelected} ${indentedClass}`}
      style={path.length > 0 ? { paddingLeft: path.length * 20 } : {}}
    >
      <div className={`item-number ${isSelected ? "selected" : ""}`}>{itemNumber}</div>
      <div className="item-divider" />
      <EditorIndexTitle
        id={id}
        index=""
        title={viewToolbars?.[id]?.viewName || ""}
        hidden={isHidden}
        selected={indexSelected}
        onNameChanged={onNavItemNameChanged}
      />
      {onNavItemToggled && (
        <div style={{ position: "relative", display: "flex", alignItems: "center", flexShrink: 0 }}>
          <button
            className="navItemVisibilityToggle"
            title={
              isHidden
                ? i18n.t("show")
                : isLastVisiblePage
                  ? i18n.t("atLeastOneSceneVisible")
                  : i18n.t("hide")
            }
            onClick={(e) => {
              e.stopPropagation();
              if (isLastVisiblePage) {
                setShowLastSceneWarning(true);
                return;
              }
              onNavItemToggled(id);
            }}
            style={{
              background: "none",
              border: "none",
              cursor: isLastVisiblePage ? "not-allowed" : "pointer",
              padding: "0 4px",
              color: isHidden ? "#888" : "#aaa",
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
            }}
          >
            {isHidden ? <LuEyeOff size={14} /> : <LuEye size={14} />}
          </button>
          {showLastSceneWarning && (
            <div
              role="alert"
              style={{
                position: "absolute",
                right: "100%",
                top: "50%",
                transform: "translateY(-50%)",
                marginRight: "6px",
                whiteSpace: "nowrap",
                background: "#c0392b",
                color: "#fff",
                fontSize: "11px",
                lineHeight: "1.2",
                padding: "4px 8px",
                borderRadius: "4px",
                boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
                zIndex: 20,
                pointerEvents: "none",
              }}
            >
              {i18n.t("atLeastOneSceneVisible")}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const ItemRenderer = (props) => {
  const {
    id,
    navItemSelected,
    indexSelected,
    connectDragSource,
    connectDragPreview,
    connectDropTarget,
    type,
    onIndexSelected,
    onNavItemSelected,
    onNavItemToggled,
    isDragging,
    isClosestDragging,
    path,
    navItems,
    multiSelected,
    multiSelectCount,
    onItemMouseDown,
    onItemClick,
  } = props;
  const collapsed = props.collapsed && type === "folder" ? "collapsed " : "";
  const selected = type === "file" && id === navItemSelected ? "selected " : " ";
  const indexSelectedClass = id === indexSelected ? "selected " : "";
  const multiSelectedClass = type === "file" && multiSelected ? "multiSelected " : "";
  const showDragBadge = type === "file" && multiSelected && multiSelectCount > 1;

  const currentItem = navItems[id];

  // Check direct parent first (skip root item id: 0)
  const directParent =
    currentItem && currentItem.parent !== undefined && currentItem.parent !== 0
      ? navItems[currentItem.parent]
      : null;
  const isDirectParentCollapsed = directParent && directParent.isExpanded === false;

  // Check all parents in the path
  const hasCollapsedAncestor =
    path &&
    path.length > 0 &&
    path.some((parentId) => {
      if (parentId === 0) return false; // Skip root
      const parent = navItems[parentId];
      return parent && parent.isExpanded === false;
    });

  // Hide item if any parent or ancestor in the hierarchy is collapsed
  const shouldHide = isDirectParentCollapsed || hasCollapsedAncestor;

  const itemElement = (
    <div
      className={`carousselContainer ${collapsed}${selected}${indexSelectedClass}${multiSelectedClass}is${type}`}
      style={{
        display: shouldHide ? "none" : "block",
        cursor: "move",
        opacity: isDragging || isClosestDragging ? 0.3 : 1,
      }}
      onMouseDown={(e) => {
        if (onItemMouseDown) onItemMouseDown(id, e);
        onIndexSelected(id);
        // e.stopPropagation();
      }}
      // Capture phase: EditorIndexTitle stops click propagation on the title
      // (most of the row), so a bubbling onClick here would miss those clicks.
      // Modifier clicks are swallowed so they don't trigger title rename mode.
      onClickCapture={(e) => {
        // Row buttons (visibility eye, section collapse) keep their own behavior.
        if (e.target.closest && e.target.closest("button")) return;
        if (onItemClick) onItemClick(id, e);
        if (e.ctrlKey || e.metaKey || e.shiftKey) e.stopPropagation();
      }}
      onDoubleClick={(e) => {
        onNavItemSelected(id);
        e.stopPropagation();
      }}
    >
      {type === "folder" && <Folder {...props} />}
      {type === "file" && <File {...props} onNavItemToggled={onNavItemToggled} />}
      {showDragBadge && (
        // Only visible while pressed (CSS :active) so it lands in the browser's
        // drag snapshot, telling the user the whole selection moves.
        <span className="multiDragBadge">{multiSelectCount}</span>
      )}
      {showDragBadge &&
        connectDragPreview &&
        // Off-screen card used as the browser drag image (react-dnd preview),
        // so dragging one selected scene visibly carries the whole selection.
        // Portaled to <body> so the scrolling index can't clip it; manual
        // offsets keep the cursor on the card despite its off-screen position.
        createPortal(
          connectDragPreview(
            <div className="multiDragPreview" aria-hidden="true">
              <span className="multiDragPreviewName">{props.name}</span>
              <span className="multiDragPreviewCount">{multiSelectCount}</span>
            </div>,
            { offsetX: 16, offsetY: 16 }
          ),
          document.body
        )}
    </div>
  );

  // Connect drag and drop functionality as required by react-sortly
  return connectDragSource && connectDropTarget
    ? connectDragSource(connectDropTarget(itemElement))
    : itemElement;
};

ItemRenderer.propTypes = {
  /**
   * Global parent of navItems (0)
   */
  id: PropTypes.any,
  /**
   * Indicates whether the carousel has been expanded or not
   */
  carouselShow: PropTypes.bool,
  /**
   *  Object containing all contained views (identified by its ID)
   */
  containedViews: PropTypes.object,
  /**
   * Selected contained view
   */
  containedViewSelected: PropTypes.any,
  /**
   * View/Contained view selected at the index
   */
  indexSelected: PropTypes.any,
  /**
   * Dictionary containing all created views, each one with its *id* as the key
   */
  navItems: PropTypes.object,
  /**
   * Current selected view (by ID)
   */
  navItemSelected: PropTypes.any,
  /**
   *  View/Contained view selected at the index
   */
  navItemsIds: PropTypes.array,
  /**
   * Callback for adding a new box
   */
  onBoxAdded: PropTypes.func,
  /**
   * Callback for selecting contained view
   */
  onContainedViewNameChanged: PropTypes.func,
  /**
   * Callback for renaming contained view
   */
  onContainedViewSelected: PropTypes.func,
  /**
   * Callback for renaming view
   */
  onIndexSelected: PropTypes.func,
  /**
   * Adds a new view
   */
  onNavItemAdded: PropTypes.func,
  /**
   * Expands navItem (only for sections)
   */
  onNavItemExpanded: PropTypes.func,
  /**
   * Callback for renaming view
   */
  onNavItemNameChanged: PropTypes.func,
  /**
   * Callback for reordering navItems
   */
  onNavItemReordered: PropTypes.func,
  /**
   * Selects a view
   */
  onNavItemSelected: PropTypes.func,
  /**
   * Toggle navItem visibility (hide/show in visor)
   */
  onNavItemToggled: PropTypes.func,
  /**
   * Scene is part of the index multi-selection
   */
  multiSelected: PropTypes.bool,
  /**
   * Size of the index multi-selection
   */
  multiSelectCount: PropTypes.number,
  /**
   * Called on mousedown before onIndexSelected (id, event)
   */
  onItemMouseDown: PropTypes.func,
  /**
   * Called on click, handles Ctrl/Cmd/Shift multi-selection (id, event)
   */
  onItemClick: PropTypes.func,
  /**
   * Object containing all the pages' toolbars
   */
  viewToolbars: PropTypes.object,
  /**
   * Object containing all the pages' toolbars
   */
  name: PropTypes.string,
  /**
   * Object containing all the pages' toolbars
   */
  type: PropTypes.oneOf(["folder", "file"]),
  /**
   * Indicates if objects is collapsed (not expanded)
   */
  collapsed: PropTypes.bool,
  /**
   * Function to connect Drag source
   */
  connectDragSource: PropTypes.func,
  /**
   * Manages preview when dragging
   */
  connectDragPreview: PropTypes.func,
  /**
   * Function to connect Drop target
   */
  connectDropTarget: PropTypes.func,
  /**
   * Boolean that indicates if object is dragging
   */
  isDragging: PropTypes.bool,
  /**
   * Boolean that indicates if closest parent is dragging
   */
  isClosestDragging: PropTypes.bool,
  /**
   *  Array of navItems path in tree to current navItem
   */
  path: PropTypes.array,
};

Folder.propTypes = {
  /**
   * Id of the current sortable element
   */
  id: PropTypes.string,
  /**
   * Name of the current sortable element
   */
  name: PropTypes.string,
  /**
   * Indicates if current sortable element is collapsed
   */
  collapsed: PropTypes.bool,
  /**
   * Index number within list of sortable containers
   */
  index: PropTypes.number,
  /**
   * Callback function for collapsing sections
   */
  onToggleCollapse: PropTypes.func,
  /**
   *  Array of navItems path in tree to current navItem
   */
  path: PropTypes.array,
  /**
   * Dictionary containing all created views, each one with its *id* as the key
   */
  navItems: PropTypes.object,
  /**
   * Callback for renaming view
   */
  onNavItemNameChanged: PropTypes.func,
  /**
   * Object containing all the pages' toolbars
   */
  viewToolbars: PropTypes.object,
  /**
   * View/Contained view selected at the index
   */
  indexSelected: PropTypes.any,
  /**
   * Selected contained view
   */
  containedViewSelected: PropTypes.any,
};
File.propTypes = {
  /**
   * Id of the current sortable element
   */
  id: PropTypes.string,
  /**
   * Name of the current sortable element
   */
  name: PropTypes.string,
  /**
   * Indicates if current sortable element is collapsed
   */
  collapsed: PropTypes.bool,
  /**
   *  Array of navItems path in tree to current navItem
   */
  path: PropTypes.array,
  /**
   * Dictionary containing all created views, each one with its *id* as the key
   */
  navItems: PropTypes.object,
  /**
   * Callback for renaming view
   */
  onNavItemNameChanged: PropTypes.func,
  /**
   * Toggle navItem visibility (hide/show in visor)
   */
  onNavItemToggled: PropTypes.func,
  /**
   * Object containing all the pages' toolbars
   */
  viewToolbars: PropTypes.object,
  /**
   * View/Contained view selected at the index
   */
  indexSelected: PropTypes.any,
  /**
   * Selected contained view
   */
  containedViewSelected: PropTypes.any,
};

export default ItemRenderer;
